import { ICON_META, isIconName as isGlyphIconName } from './iconMeta'
import type { IconName as GlyphIconName } from './iconMeta'
import { COMPONENT_ICON_META, isComponentIconName } from './componentIcons'
import type { ComponentIconName } from './componentIcons'

/**
 * A picker icon is either a curated glyph (raw geometry, lazily loaded and
 * tinted via `currentColor`) or a channel/integration icon (an existing Popcorn
 * icon component). Both are addressed by a stable kebab-case name persisted in
 * saved-view identities. The two name sets are disjoint (asserted in tests).
 */
export type IconName = GlyphIconName | ComponentIconName

/** Search-alias map spanning both glyph and component icons. */
export const ICON_META_ALL = {
  ...ICON_META,
  ...COMPONENT_ICON_META,
} satisfies Record<IconName, readonly string[]>

/**
 * Grid order: the curated glyphs first (`bookmark` leads as the design default),
 * then the channel and integration icons appended after. `Object.keys` is typed
 * as `string[]`; the casts restore the concrete name unions, which is sound
 * because the keys are exactly `keyof` each meta map.
 */
export const ICON_NAMES: readonly IconName[] = [
  ...(Object.keys(ICON_META) as GlyphIconName[]),
  ...(Object.keys(COMPONENT_ICON_META) as ComponentIconName[]),
]

/**
 * Narrows an arbitrary string to an {@link IconName}. Handy at boundaries such
 * as a persisted value that may name an icon no longer offered.
 */
export function isIconName(value: string): value is IconName {
  return isGlyphIconName(value) || isComponentIconName(value)
}
